<script lang="ts">
import type { CreateElement, VNode, VNodeChildren, PropType } from "vue";
import { defineComponent } from "vue";
import type {
  BmGridColumnConfigurations,
  BmGridConfiguration,
  BmGridExportEvent,
  ContentRow,
  Row,
  RowGroup,
} from "@/components/controls/grid/BmGridData";
import {
  BmGridState,
  buildVirtualGrid,
  computeNextSelectedItemKeys,
  extractItemKey,
  findInvalidItemKeyReason,
  getFlattenedRows,
  getGroupedColumns,
  getVisibleColumns,
  isContentRow,
  logInvalidItemKeys,
  migrateGridState,
  sanitizeAndSyncGridState,
} from "@/components/controls/grid/BmGridData";
import BmGridStateRepository from "@/components/controls/grid/state/BmGridStateRepository";
import { cloneDeep } from "lodash-es";
import BmGridTableView from "@/components/controls/grid/BmGridTableView.vue";
import BmGridCardView from "@/components/controls/grid/BmGridCardView.vue";
import type { DnDEvent } from "vue-easy-dnd";
import type { Location } from "vue-router";

import type { AgGridInterface } from "@/components/controls/grid/AgGrid";
import AgGrid from "@/components/controls/grid/AgGrid";
import type { DocGenGridShape } from "@/components/controls/grid/BmGridDocumentExport";
import {
  buildDocGenGridFromAgGridRows,
  buildDocGenGridFromVirtualRows,
  downloadGridCsv,
  downloadGridPdf,
  exportGridToGoogleSheets,
  getExportColumnEntries,
} from "@/components/controls/grid/BmGridDocumentExport";
import { isRunningEndToEndTests } from "@/app/ui/utilities";
import { activeGridRegistry } from "@/components/controls/grid/activeGridRegistry";
import { screenSize } from "@/vue23switching/screenSize";
import type { GridGroupValue } from "@/app/services/generated/documentgenerationService";

export interface GridDragDropEvent {
  /** Row key that was dragged */
  draggedRowKeys: string[];
  /** The row key that the item should be placed under. If undefined, the item should be placed at the top of the list.*/
  droppedOnRowKey: string | undefined;
}

type GridSlotFn = (props?: unknown) => VNodeChildren;

//
// <script setup> Conversion Warning
// If this is ever converted to <script setup> Then we need to EXPOSE getFilteredItems.
//

/**
 * Brewman Grid component.
 *
 * Dragging only works on desktop view
 */
export default defineComponent({
  name: "BmGrid",
  props: {
    itemKey: {
      type: Function as PropType<(item: any) => string>,
      required: false,
    },
    /**
     * The current user modifiable state of the grid (sorting, grouping, filtering, etc). You can specify the initial
     * state of the grid, and also use this property to load/save state if desired.
     */
    gridState: {
      type: Object as PropType<BmGridState>,
      default: () => BmGridStateRepository.getEphemeral(),
    },
    /**
     * The configuration (types, keys, etc.) of the columns in the grid and the default state (filters).
     */
    gridConfiguration: {
      type: Object as PropType<BmGridConfiguration>,
      required: true,
    },
    /**
     * The array of items to display in the grid. The array is not modified by this grid.
     */
    items: {
      type: Array as PropType<readonly any[]>,
      required: true,
    },
    /**
     * Whether expandable rows are defaulted to expanded.
     */
    defaultExpandExpandable: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether group rows are defaulted to expanded.
     */
    defaultExpandGroups: {
      type: Boolean,
      default: true,
    },
    /**
     * The plural noun of the items displayed in the grid.
     */
    itemNoun: {
      type: String,
      default: "",
    },
    selectedItemKeys: {
      type: Array as PropType<readonly string[]>,
      default: () => [],
    },
    expandedItemKeys: {
      type: Array as PropType<readonly string[]>,
      default: () => [],
    },
    // Only allows one row to be expanded (top-level)
    // will require expanded-item-keys.sync
    accordian: {
      type: Boolean,
      required: false,
      default: false,
    },
    rowSelectable: {
      type: Function as PropType<(item: any) => boolean>,
      default: (_item: any) => true,
    },
    allowSort: {
      type: Boolean,
      required: false,
      default: true,
    },
    allowExpand: {
      type: Boolean,
      default: false,
    },
    allowSelect: {
      type: Boolean,
      default: false,
    },
    allowDrag: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether the draggable rows can be dropped back inside this grid
     */
    allowDrop: {
      type: Boolean,
      default: false,
    },
    rowDraggable: {
      type: Function as PropType<(item: any) => boolean>,
      default: (item: any) => true,
    },
    rowExpandable: {
      type: Function as PropType<(item: any) => boolean>,
      default: (item: any) => false,
    },
    subGrid: {
      type: Boolean,
      default: false,
    },
    cardColumns: {
      type: Number,
      default: 2,
    },
    height: {
      type: [Number, String],
      default: null,
    },
    overrideHeight: {
      type: [Number, String],
      default: null,
    },
    allowCardView: {
      type: Boolean,
      default: true,
    },
    /**
     * Provide a function that returns an array of classes to add to the row element of the grid.
     * Note. This only supports content rows (not grouping rows).
     */
    rowClasses: {
      type: Function as PropType<(row: any) => string[]>,
      default: (row: any) => [],
    },
    /**
     * Provide a function that returns a Location;
     * This will be used to wrap the td elements in router-links for redirects
     */
    rowClickRoute: {
      type: Function as PropType<((item: any) => Location | null) | null>,
      default: null,
    },
    /**
     * Restricts the number of visible rows the grid component will initially display.
     * Note. this is not a filter and does not effect filter counts.
     *
     * The user can remove this restriction, but cannot re-apply it.
     */
    initialVisibleRows: {
      type: Number,
      default: null,
    },
    disableAgGrid: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    disableCardView: {
      type: Boolean,
      required: false,
      default: false,
    },
    isFixedShapeReport: {
      type: Boolean,
      required: false,
      default: false,
    },
    includeIds: {
      type: Boolean,
      required: false,
      default: false,
    },
    rowAutoHeight: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      virtualGrid: [] as readonly Row[],
      gridStateInternal: new BmGridState() as BmGridState,
      disableReactiveRows: false as boolean,
      hasNonStringOrDuplicateKeys: false as boolean,
      showAllRows: false,
      currentDraggedRow: null as Row | null,
      /**
       * On Firefox, a parent anchor tag component is clicked even if a child
       * input element prevents event propagation. This means the row will always
       * be clicked if it has a route.
       *
       * For now, disable row click on the first table cell if using firefox.
       *
       * Progress: https://github.com/vuejs/vue/issues/12490
       */
      shouldAllowRowClickRouteOnSelectableCell: true as boolean,
      /** Populated by mapGridToDocGenGridShape for Print All callers */
      pdfTitle: "" as string,
      pdfColumns: [] as string[],
      pdfGrid: [] as readonly GridGroupValue[],
    };
  },
  computed: {
    hasGrouping(): boolean {
      return Object.keys(this.computedGroupedColumns).length > 0;
    },
    visibleColumns(): BmGridColumnConfigurations {
      return getVisibleColumns(
        this.gridConfiguration,
        this.gridState,
        this.computedGroupedColumns
      );
    },
    computedGroupedColumns(): BmGridColumnConfigurations {
      return getGroupedColumns(this.gridConfiguration, this.gridState);
    },
    visibleRowLimit(): number | null {
      // Truncate rows if the user has not asked for all rows, and there are no groups or footer rows.
      if (
        !this.showAllRows &&
        Object.keys(this.computedGroupedColumns).length === 0 &&
        !Object.values(this.visibleColumns).some((col) => col.footer)
      ) {
        return this.initialVisibleRows;
      }

      return null;
    },
    isAgGridActive(): any {
      // @TODO: Implement AG Grid in e2e tests when dom unmounting works:
      //  https://github.com/cypress-io/cypress/issues/7306
      return (
        !this.disableAgGrid &&
        !this.isCardView &&
        !this.allowDrag &&
        !this.allowDrop &&
        !isRunningEndToEndTests() // Use BmGrid for all E2E tests
      );
    },
    isCardView(): boolean {
      // We revert to desktop view if the number of groupings exceed 2 as card view becomes hard to read at that stage
      return (
        !this.disableCardView &&
        screenSize.xs &&
        Object.keys(this.computedGroupedColumns).length < 3 &&
        this.allowCardView
      );
    },
    draggableItems(): readonly string[] {
      if (this.currentDraggedRow?.isSelected) {
        return this.selectedItemKeys;
      } else {
        return this.currentDraggedRow ? [this.currentDraggedRow.rowKey] : [];
      }
    },
    isRowClickable(): any {
      return !!this.gridConfiguration.onRowClick || !!this.rowClickRoute;
    },
    gridHeightWithUnits() {
      if (!this.height) {
        return undefined;
      }

      if (typeof this.height === "string") {
        return this.height;
      }

      if (typeof this.height === "string") {
        return this.height;
      }

      return isNaN(this.height) ? this.height : `${this.height}px`;
    },
  },
  watch: {
    isAgGridActive: {
      // @ts-ignore
      handler: "activeGridWatcher",
      immediate: true,
    },
  },
  mounted() {
    activeGridRegistry.register(this);
  },
  created() {
    const isFirefox = navigator.userAgent.toLowerCase().indexOf("firefox") > -1;
    this.shouldAllowRowClickRouteOnSelectableCell = !(
      isFirefox && this.allowSelect
    );

    migrateGridState(this.gridState, this.gridConfiguration);
    this.gridStateInternal = cloneDeep(this.gridState);

    this.$watch("gridState", this.updateGridState, {
      immediate: true,
      deep: true,
    });

    this.$watch("gridConfiguration", this.refreshVirtualGrid, { deep: true });
    this.$watch("gridStateInternal", this.refreshVirtualGrid, { deep: true });
    this.$watch("selectedItemKeys", this.refreshVirtualGrid, { deep: true });

    (
      [
        "defaultExpandExpandable",
        "defaultExpandGroups",
        "expandedItemKeys",
      ] as const
    ).forEach((prop) =>
      this.$watch(prop, () => this.refreshVirtualGrid(false))
    );

    this.$watch(
      "items",
      (newItems: any[], oldItems: any[]) => {
        this.refreshVirtualGrid(true);
        this.checkItemsForDuplicateOrUndefinedKeys(newItems, oldItems);
      },
      { deep: true }
    );

    this.refreshVirtualGrid(false);
  },
  beforeDestroy() {
    activeGridRegistry.unregister(this);
  },
  methods: {
    /**
     * Vue 2.7: $scopedSlots is the function map (includes normal slots).
     * Vue 3: only $slots exists, and it is already the function map.
     */
    getSlots(): Record<string, GridSlotFn | undefined> {
      return (this.$scopedSlots ?? this.$slots) as Record<
        string,
        GridSlotFn | undefined
      >;
    },
    getExpandSlot(): GridSlotFn | undefined {
      return this.getSlots()["expand"];
    },
    getNoDataSlot(): GridSlotFn | undefined {
      return this.getSlots()["no-data"];
    },
    getSummarySlot(): GridSlotFn | undefined {
      return this.getSlots()["summary"];
    },
    activeGridWatcher(isAgGridActive: boolean) {
      this.setDisableReactiveRows(isAgGridActive);
    },
    refreshVirtualGrid(reapplyRowStates: boolean = true) {
      if (this.disableReactiveRows) {
        return;
      }

      const result = buildVirtualGrid({
        items: this.items,
        gridConfiguration: this.gridConfiguration,
        gridState: this.gridState,
        itemKey: this.itemKey,
        allowSort: this.allowSort,
        selectedItemKeys: this.selectedItemKeys,
        expandedItemKeys: this.expandedItemKeys,
        defaultExpandExpandable: this.defaultExpandExpandable,
        defaultExpandGroups: this.defaultExpandGroups,
        itemNoun: this.itemNoun,
        reapplyRowStates,
        previousVirtualGrid: this.virtualGrid,
      });

      this.$emit("filter-summary", result.filterSummary);
      this.virtualGrid = result.rows;
    },
    updateGridState(): void {
      const nextInternal = sanitizeAndSyncGridState(
        this.gridState,
        this.gridConfiguration,
        this.gridStateInternal
      );
      if (nextInternal) {
        this.gridStateInternal = nextInternal;
      }
    },
    setDisableReactiveRows(value: boolean) {
      this.disableReactiveRows = value;
      if (!value) {
        this.refreshVirtualGrid();
      }
    },
    toggleSelectedRow(row: Row | undefined) {
      this.$emit(
        "update:selected-item-keys",
        computeNextSelectedItemKeys(
          row,
          this.selectedItemKeys,
          this.virtualGrid,
          this.rowSelectable
        )
      );
    },
    getFlattenedRows(rootNode: readonly Row[]): Row[] {
      return getFlattenedRows(rootNode);
    },
    checkItemsForDuplicateOrUndefinedKeys(newItems: any[], oldItems: any[]) {
      if (
        oldItems.length !== 0 ||
        !newItems.length ||
        this.hasNonStringOrDuplicateKeys
      ) {
        return;
      }

      const reason = findInvalidItemKeyReason(newItems, (item) =>
        extractItemKey(item, this.gridConfiguration, this.itemKey)
      );
      if (reason) {
        this.hasNonStringOrDuplicateKeys = true;
        logInvalidItemKeys(reason);
      }
    },
    async handleExport(event: BmGridExportEvent) {
      if (event.stateId && event.stateId == this.gridState.stateId) {
        if (event.format == "csv") {
          const columnEntries = getExportColumnEntries(
            this.visibleColumns,
            this.computedGroupedColumns,
            {
              includeGroupedColumns: true,
              columnOrder: this.gridState.columnOrder,
              orderForAgGrid: this.isAgGridActive,
            }
          );
          const columns = columnEntries.map((col) => col[1]);

          let items: readonly any[];
          if (this.isAgGridActive && this.$refs.agGridRef) {
            items = (
              (
                this.$refs.agGridRef as unknown as AgGridInterface
              )?.getSortedAndFilteredRows() ?? []
            )
              .filter((row) => !row.group)
              .map((row) => row.data);
          } else {
            items = this.getFlattenedRows(this.virtualGrid)
              .filter((r) => isContentRow(r))
              .map((r) => (r as ContentRow).item);
          }

          downloadGridCsv(event.filename, columns, items);
        } else if (event.format == "pdf" || event.format == "googleSheets") {
          const shape = this.buildDocGenGridShape(event.fileTitle);

          if (event.format == "pdf") {
            downloadGridPdf(event.filename, shape);
          } else {
            await exportGridToGoogleSheets(shape);
          }
        }
      }
    },
    /**
     * Build a document-generation shape for this grid's current visible rows.
     * Used by single-grid export and by multi-grid "Print All" pages via ref.
     */
    buildDocGenGridShape(title: string): DocGenGridShape {
      const visibleDataColumns = getExportColumnEntries(
        this.visibleColumns,
        this.computedGroupedColumns,
        {
          includeGroupedColumns: false,
          columnOrder: this.gridState.columnOrder,
          orderForAgGrid: this.isAgGridActive,
        }
      );

      return this.isAgGridActive && this.$refs.agGridRef
        ? buildDocGenGridFromAgGridRows({
            title,
            visibleDataColumns,
            agGridRows:
              (
                this.$refs.agGridRef as unknown as AgGridInterface
              )?.getSortedAndFilteredRows() ?? [],
          })
        : buildDocGenGridFromVirtualRows({
            title,
            visibleDataColumns,
            groupedColumns: this.computedGroupedColumns,
            rows: this.virtualGrid,
          });
    },
    /**
     * Populates pdfTitle / pdfColumns / pdfGrid for callers that print multiple
     * grids via component refs (Loading / Packing / Picking lists, Stock Requirement).
     */
    mapGridToDocGenGridShape(title: string): void {
      const shape = this.buildDocGenGridShape(title);
      this.pdfTitle = shape.title;
      this.pdfColumns = [...shape.columnTitles];
      this.pdfGrid = shape.grid;
    },
    genTableView(h: CreateElement): VNode {
      const expandSlot = this.getExpandSlot();
      const noDataSlot = this.getNoDataSlot();

      return h(BmGridTableView, {
        props: {
          virtualGrid: this.virtualGrid,
          visibleColumns: this.visibleColumns,
          groupedColumns: this.computedGroupedColumns,
          sortBy: this.gridState.sortBy,
          allowSort: this.allowSort,
          allowSelect: this.allowSelect,
          allowDrag: this.allowDrag,
          allowDrop: this.allowDrop,
          allowExpand: this.allowExpand,
          subGrid: this.subGrid,
          draggableItems: this.draggableItems,
          visibleRowLimit: this.visibleRowLimit,
          rowDraggable: this.rowDraggable,
          rowExpandable: this.rowExpandable,
          rowSelectable: this.rowSelectable,
          rowClasses: this.rowClasses,
          rowClickRoute: this.rowClickRoute,
          isRowClickable: !!this.isRowClickable,
          allowRowClickRouteOnFirstCell:
            this.shouldAllowRowClickRouteOnSelectableCell,
        },
        on: {
          "toggle-select-all": () => this.toggleSelectedRow(undefined),
          "toggle-select": (row: Row) => this.toggleSelectedRow(row),
          "sort-column": (payload: {
            columnKey: string;
            shiftKey: boolean;
          }) => {
            this.gridState.applyColumnSort(payload.columnKey, payload.shiftKey);
          },
          drop: (payload: { row: Row | null; event: DnDEvent }) => {
            this.onRowDrop(payload.row, payload.event);
          },
          dragover: (row: ContentRow) => this.onDragOver(row),
          "drag-start": (row: ContentRow) => {
            this.currentDraggedRow = row;
          },
          "toggle-expand": (row: ContentRow) => {
            row.isExpanded = !row.isExpanded;
            this.refreshVirtualGrid();
            this.$emit("expand-row", row);

            if (this.accordian) {
              this.$emit(
                "update:expanded-item-keys",
                row.isExpanded ? [row.rowKey] : []
              );
            }
          },
          "toggle-group-expand": (group: RowGroup) => {
            group.isExpanded = !group.isExpanded;
            this.refreshVirtualGrid();
            this.$emit("expand-row", group);

            if (this.accordian) {
              this.$emit(
                "update:expanded-item-keys",
                group.isExpanded ? [group.rowKey] : []
              );
            }
          },
          "show-all": () => {
            this.showAllRows = true;
          },
          "row-click": (row: ContentRow) => this.onRowClick(row),
        },
        scopedSlots: {
          ...(expandSlot ? { expand: expandSlot } : {}),
          ...(noDataSlot ? { "no-data": noDataSlot } : {}),
        } as any,
      });
    },
    genCardView(h: CreateElement): VNode {
      const expandSlot = this.getExpandSlot();
      const noDataSlot = this.getNoDataSlot();

      return h(BmGridCardView, {
        props: {
          virtualGrid: this.virtualGrid,
          visibleColumns: this.visibleColumns,
          groupedColumns: this.computedGroupedColumns,
          allowSelect: this.allowSelect,
          allowExpand: this.allowExpand,
          cardColumns: this.cardColumns,
          visibleRowLimit: this.visibleRowLimit,
          rowExpandable: this.rowExpandable,
          rowSelectable: this.rowSelectable,
          rowClasses: this.rowClasses,
          isRowClickable: !!this.isRowClickable,
        },
        on: {
          "toggle-select": (row: Row) => this.toggleSelectedRow(row),
          "toggle-expand": (row: ContentRow, isExpanded: boolean) => {
            row.isExpanded = isExpanded;
            this.refreshVirtualGrid();
            this.$emit("expand-row", row);

            if (this.accordian) {
              this.$emit(
                "update:expanded-item-keys",
                isExpanded ? [row.rowKey] : []
              );
            }
          },
          "toggle-group-expand": (group: RowGroup) => {
            // Card group expand historically only refreshed virtual grid
            // (no expand-row / accordion emits).
            group.isExpanded = !group.isExpanded;
            this.refreshVirtualGrid();
          },
          "show-all": () => {
            this.showAllRows = true;
          },
          "row-click": (row: ContentRow) => this.onRowClick(row),
        },
        scopedSlots: {
          ...(expandSlot ? { expand: expandSlot } : {}),
          ...(noDataSlot ? { "no-data": noDataSlot } : {}),
        } as any,
      });
    },
    onRowDrop(row: Row | null, event: DnDEvent) {
      let keyAbove = undefined;
      if (row) {
        keyAbove = row.rowKey;
      }
      this.$emit("drag-drop", {
        draggedRowKeys: event.data,
        droppedOnRowKey: keyAbove,
      } as GridDragDropEvent);
    },
    onDragOver(row: Row | null) {
      // get element being dragged over
      if (row) {
        row.dropBelow = true;
      }
    },
    genSummarySlot(h: CreateElement): VNodeChildren {
      return [
        h(
          "div",
          {
            class: "grid-summary text-right",
            attrs: {},
          },
          this.getSummarySlot()?.({})
        ),
      ];
    },
    onRowClick(row: ContentRow) {
      this.gridConfiguration.onRowClick?.(row.item);
    },
    getFilteredItems() {
      if (this.isAgGridActive && this.$refs.agGridRef) {
        const agGridRows =
          (
            this.$refs.agGridRef as unknown as AgGridInterface
          )?.getSortedAndFilteredRows() ?? [];
        return agGridRows.map((row) => row.data);
      } else {
        const contentRows = this.getFlattenedRows(this.virtualGrid).filter(
          (r) => isContentRow(r)
        ) as ContentRow[];
        return contentRows.map((row: ContentRow) => row.item);
      }
    },
  },
  render(h: CreateElement): VNode {
    if (this.isAgGridActive) {
      return h("div", {}, [
        h(AgGrid, {
          props: {
            ...this.$props,
            height: this.gridHeightWithUnits,
            expandSlotRenderFn: this.getExpandSlot(),
            itemKey: this.gridConfiguration.itemKey ?? this.itemKey,
            rowAutoHeight: this.rowAutoHeight,
          },
          ref: "agGridRef",
          on: {
            "filter-summary": (event: Event) =>
              this.$emit("filter-summary", event),
            "drag-drop": (event: GridDragDropEvent) =>
              this.$emit("drag-drop", event),
            "update:selected-item-keys": (event: any[]) =>
              this.$emit("update:selected-item-keys", event),
            "expand-row": (event: any) => this.$emit("expand-row", event),
          },
        }),
        this.getSummarySlot() ? this.genSummarySlot(h) : "",
      ]);
    }
    return h(
      "div",
      {
        style: {
          "max-height": this.isCardView ? "100vh" : this.gridHeightWithUnits,
          "overflow-y": "auto", // To prevent overflow in mobile view
        },
        class: {
          "bm-grid": true,
          "bm-grid-card-view": this.isCardView,
          "bm-grid-table-view": !this.isCardView,
          grouped: this.hasGrouping,
          "sub-grid": this.subGrid,
        },
      },
      [
        this.isCardView ? this.genCardView(h) : this.genTableView(h),
        this.getSummarySlot() ? this.genSummarySlot(h) : "",
      ]
    );
  },
});
</script>

<style scoped lang="sass">
@import "src/sass/typography"
@import "src/sass/colours"

@mixin contents-text
  @include bm-body2
  color: $bm-color-text01


// Remove a-tag styling for the grid rows
.row-clickable a
  text-decoration: none
  color: $bm-color-text01

// Table view styles
.bm-grid-table-view
  overflow-x: auto

.bm-grid-table-view table
  width: 100%
  border-collapse: separate
  border-spacing: 0


// Pad out content cells to match parents and give spacing
.content-cell
  padding: 8px
  overflow: hidden
  text-overflow: ellipsis

.bm-grid-table-view td
  @include contents-text
  border-bottom: 1px solid #D1CFD7

.bm-grid-table-view tr
  height: 36px

// Row/Card colours
.bm-grid .content.even
  background-color: $bm-color-background01

.bm-grid .content:not(.even)
  background-color: $bm-color-background04

.bm-grid .group.group-index-0 > td
  color: $bm-color-text04

.bm-grid .group.group-index-0, .bm-grid .group.group-index-0 .v-icon
  background-color: #73777A
  color: $bm-color-text04

.bm-grid .group.group-index-1 > td
  color: $bm-color-text04

.bm-grid .group.group-index-1, .bm-grid .group.group-index-1 .v-icon
  background-color: #989898
  color: white

// expander arrow inherit row/card text colour
.bm-grid i.expand-icon
  color: inherit

.bm-grid .expand
  background-color: transparent

// position cell block content next to the expander icon
.expandable-cell
  display: flex

.bm-grid-table-view .hidden-icon
  visibility: hidden

// Tidier to remove expander in card view (icon may still appear via shared markup)
.bm-grid-card-view :deep(.hidden-icon)
  display: none

// These rules make the subgrid transparent and deep margin'd
// Use margin not padding so that content isn't visible behind sticky header scrolling
.bm-grid-table-view .sub-grid
  margin: 20px 30px

div.sub-grid table tr, div.sub-grid table thead tr
  background: transparent !important

.grid-summary
  background: $bm-color-grid-summary
  margin-top: 16px
  padding: 16px

.grid-summary ul
  list-style: none

// Animating the grid on expand

@keyframes slideIn
  0%
    opacity: 0
    transform: translateY(-20px)

  100%
    opacity: 1
    transform: translateY(0)

:deep(.animateIn)
  overflow: hidden
  animation: slideIn .3s ease-out
  transition: height .3s ease-out

:deep(.animateIn tbody)
  overflow: hidden
  animation: slideIn .4s ease-out

// Useful row classes clients can use (table + card child)

table tbody tr.grid-row-error, .bm-grid-card-view :deep(div.card.grid-row-error)
  background-color: $bm-color-incomplete-row !important

table tbody tr.grid-row-warn, .bm-grid-card-view :deep(div.card.grid-row-warn)
  background-color: $bm-color-warn-row !important

table tbody tr.grid-row-success, .bm-grid-card-view :deep(div.card.grid-row-success)
  background-color: $bm-color-successful-row !important

.bm-grid :deep(.bm-grid-clickable)
  text-decoration: underline
  text-underline-position: under
  text-decoration-style: dotted
  text-decoration-color: darkgrey
  cursor: pointer
  padding-bottom: 1px
// Ensure the underline is always visible

.bm-grid :deep(p)
  width: 100%
  margin: 0

.bm-grid-row-controls
  display: flex
  align-items: center

.row-clickable td
  cursor: pointer
  transition: background .1s ease-in-out

.row-clickable:hover td
  background: $bm-color-text03

// The copy of the dragged item at the final location
.sortable-ghost
  opacity: 0.5

// Hide the dragging ghost image as it doesn't make much sense - especially when multi dragging
.sortable-drag
  opacity: 0

// A line under a dragged over row to show where items will end up
.drop-in td, .drop-in th
  border-bottom: 3px solid
  border-top: inherit
  transition: 0.1s

.drop-in.drop-above td
  border-top: 3px solid
  border-bottom: inherit
  transition: 0.1s

.expand-icon
  user-select: none
  cursor: pointer

.expand-icon-active
  transform: rotate(-180deg)

@media print
  /* Expand all information in a BM-Grid */
  .bm-grid
    max-height: 100% !important
    overflow: visible !important

  .expand-icon
    display: none
</style>
